<template>
    <div class="voice-recorder">
        <button
            :class="['voice-btn', { 
                'recording': isRecording, 
                'connecting': isConnecting,
                'connecting-websocket': isConnectingWebSocket
            }]"
            @mousedown="handleMouseDown"
            @mouseup="handleMouseUp"
            @mouseleave="handleMouseLeave"
            @touchstart="handleTouchStart"
            @touchend="handleTouchEnd"
            :disabled="isConnecting || isConnectingWebSocket"
            :title="getButtonText()"
        >
            <img 
                :src="microphoneIcon" 
                alt="语音输入" 
                class="microphone-icon"
            />
        </button>
        
        <!-- 连接进度提示 -->
        <div v-if="isConnectingWebSocket" class="connection-progress">
            <div class="progress-bar">
                <div class="progress-fill" :style="{ width: connectionProgress + '%' }"></div>
            </div>
            <div class="progress-text">连接中...</div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'VoiceRecorder',
    props: {
        // 接收识别结果回调
        onResult: {
            type: Function,
            default: () => {}
        }
    },
    data() {
        return {
            isRecording: false,
            isConnecting: false,
            isConnectingWebSocket: false,
            recordingTime: 0,
            recorder: null,
            speechService: null,
            timeInterval: null,
            isWebSocketConnected: false,
            isButtonPressed: false,
            connectionProgress: 0,
            recognizedText: '', // 存储识别到的文本
            isRecognitionComplete: false, // 标记识别是否完成
            audioChunks: [], // 存储音频数据块
            audioDuration: 0, // 音频时长（秒）
            audioBlob: null, // 最终的音频Blob
            connectDelayTimer: null,
            connectionTimeoutId: null,
            connectionProgressIntervalId: null
        }
    },
    computed: {
        microphoneIcon() {
            if (this.isRecording || this.isButtonPressed) {
                return require('@/assets/images/svg/microphoneClick.svg')
            } else {
                return require('@/assets/images/svg/microphone.svg')
            }
        }
    },
    async mounted() {
        // 等待RecorderManager加载
        await this.waitForRecorderManager()
        this.initServices()
    },
    beforeUnmount() {
        this.cleanup()
    },
    methods: {
        // 等待RecorderManager加载
        waitForRecorderManager() {
            return new Promise((resolve) => {
                const check = () => {
                    if (window.RecorderManager) {
                        resolve()
                    } else {
                        setTimeout(check, 100)
                    }
                }
                check()
            })
        },

        initServices() {
            // 预加载SpeechService - 提高后续连接速度
            if (!this.speechService) {
                import('../services/speechService.js').then(module => {
                    this.speechService = new module.SpeechService()
                    console.log('SpeechService预加载完成')
                }).catch(error => {
                    console.error('SpeechService加载失败:', error)
                })
            }
        },

        scheduleConnectWebSocket() {
            this.clearConnectDelay()
            this.connectDelayTimer = setTimeout(() => {
                if (this.isButtonPressed && !this.isRecording && !this.isConnectingWebSocket) {
                    this.connectWebSocket()
                }
            }, 150)
        },

        clearConnectDelay() {
            if (this.connectDelayTimer) {
                clearTimeout(this.connectDelayTimer)
                this.connectDelayTimer = null
            }
        },

        // 连接WebSocket（按住时调用）
        async connectWebSocket() {
            if (!this.isButtonPressed) {
                console.log('按钮已松开，取消WebSocket连接')
                return
            }

            if (this.isConnectingWebSocket) {
                return
            }

            // 先关闭之前的连接（如果有的话）
            if (this.speechService && this.speechService.ws) {
                console.log('关闭之前的WebSocket连接...')
                this.speechService.close()
            }

            this.isConnectingWebSocket = true
            this.isWebSocketConnected = false
            this.connectionProgress = 0

            if (this.connectionTimeoutId) {
                clearTimeout(this.connectionTimeoutId)
                this.connectionTimeoutId = null
            }
            if (this.connectionProgressIntervalId) {
                clearInterval(this.connectionProgressIntervalId)
                this.connectionProgressIntervalId = null
            }

            // 连接超时机制 - 最多等待5秒
            this.connectionTimeoutId = setTimeout(() => {
                if (this.isConnectingWebSocket) {
                    console.error('WebSocket连接超时')
                    this.isConnectingWebSocket = false
                    this.connectionProgress = 0
                    if (this.connectionProgressIntervalId) {
                        clearInterval(this.connectionProgressIntervalId)
                        this.connectionProgressIntervalId = null
                    }
                    this.showConnectionError('连接超时，请重试')
                }
            }, 5000)

            // 优化的连接进度动画 - 更快更真实
            this.connectionProgressIntervalId = setInterval(() => {
                if (this.connectionProgress < 95) {
                    // 前80%快速增长，后15%缓慢增长
                    if (this.connectionProgress < 80) {
                        this.connectionProgress += Math.random() * 15 + 10
                    } else {
                        this.connectionProgress += Math.random() * 5 + 2
                    }
                }
            }, 40) // 更快的更新频率

            try {
                // 并行处理：同时等待SpeechService和开始连接
                console.log('开始建立新的WebSocket连接...')
                
                // 如果SpeechService还没准备好，先等待
                if (!this.speechService) {
                    await this.waitForSpeechService()
                }
                
                this.speechService.connectWebSocket(
                    // onMessage
                    (text, jsonData) => {
                        // 打印识别结果，但不立即发送
                        console.log('语音识别结果:', text)
                        console.log('识别数据:', jsonData)
                        this.recognizedText = text
                        this.isRecognitionComplete = true
                    },
                    // onOpen
                    () => {
                        console.log('WebSocket连接成功')
                        this.isWebSocketConnected = true
                        this.isConnectingWebSocket = false
                        this.connectionProgress = 100
                        if (this.connectionProgressIntervalId) {
                            clearInterval(this.connectionProgressIntervalId)
                            this.connectionProgressIntervalId = null
                        }
                        if (this.connectionTimeoutId) {
                            clearTimeout(this.connectionTimeoutId)
                            this.connectionTimeoutId = null
                        }
                        // 连接成功后，如果用户还在按住按钮，则开始录音
                        if (this.isButtonPressed) {
                            this.startRecording()
                        } else {
                            console.log('按钮已松开，连接建立后取消录音')
                            this.cancelPendingRecording()
                        }
                    },
                    // onError
                    (error) => {
                        console.error('WebSocket错误:', error)
                        this.isWebSocketConnected = false
                        this.isConnectingWebSocket = false
                        this.connectionProgress = 0
                        if (this.connectionProgressIntervalId) {
                            clearInterval(this.connectionProgressIntervalId)
                            this.connectionProgressIntervalId = null
                        }
                        if (this.connectionTimeoutId) {
                            clearTimeout(this.connectionTimeoutId)
                            this.connectionTimeoutId = null
                        }
                        this.showConnectionError('连接失败，请重试')
                    },
                    // onClose
                    (event) => {
                        console.log('WebSocket连接关闭:', event.code, event.reason)
                        this.isWebSocketConnected = false
                        this.isConnectingWebSocket = false
                        this.connectionProgress = 0
                        if (this.connectionProgressIntervalId) {
                            clearInterval(this.connectionProgressIntervalId)
                            this.connectionProgressIntervalId = null
                        }
                        if (this.connectionTimeoutId) {
                            clearTimeout(this.connectionTimeoutId)
                            this.connectionTimeoutId = null
                        }
                    }
                )
            } catch (error) {
                console.error('连接WebSocket失败:', error)
                this.isConnectingWebSocket = false
                this.connectionProgress = 0
                if (this.connectionProgressIntervalId) {
                    clearInterval(this.connectionProgressIntervalId)
                    this.connectionProgressIntervalId = null
                }
                if (this.connectionTimeoutId) {
                    clearTimeout(this.connectionTimeoutId)
                    this.connectionTimeoutId = null
                }
                this.showConnectionError('连接失败，请重试')
            }
        },

        // 等待SpeechService初始化 - 优化等待时间
        waitForSpeechService() {
            return new Promise((resolve, reject) => {
                let attempts = 0
                const maxAttempts = 50 // 最多等待5秒
                
                const checkService = () => {
                    if (this.speechService) {
                        resolve()
                    } else if (attempts >= maxAttempts) {
                        reject(new Error('SpeechService初始化超时'))
                    } else {
                        attempts++
                        setTimeout(checkService, 50) // 更频繁的检查
                    }
                }
                checkService()
            })
        },

        // 显示连接错误
        showConnectionError(message) {
            console.error(message)
            // 可以在这里添加错误提示UI
        },

        // 鼠标按下事件
        handleMouseDown(event) {
            event.preventDefault()
            if (!this.isButtonPressed && !this.isRecording && !this.isConnectingWebSocket) {
                this.isButtonPressed = true
                this.scheduleConnectWebSocket()
            }
        },

        // 鼠标松开事件
        handleMouseUp(event) {
            event.preventDefault()
            this.clearConnectDelay()

            const wasPressed = this.isButtonPressed
            this.isButtonPressed = false

            if (this.isRecording) {
                this.stopRecording()
            } else if (wasPressed) {
                this.cancelPendingRecording()
            }
        },

        // 鼠标离开事件
        handleMouseLeave(event) {
            event.preventDefault()
            this.clearConnectDelay()

            const wasPressed = this.isButtonPressed
            this.isButtonPressed = false

            if (this.isRecording) {
                this.stopRecording()
            } else if (wasPressed) {
                this.cancelPendingRecording()
            }
        },

        // 触摸开始事件
        handleTouchStart(event) {
            event.preventDefault()
            if (!this.isButtonPressed && !this.isRecording && !this.isConnectingWebSocket) {
                this.isButtonPressed = true
                this.scheduleConnectWebSocket()
            }
        },

        // 触摸结束事件
        handleTouchEnd(event) {
            event.preventDefault()
            this.clearConnectDelay()

            const wasPressed = this.isButtonPressed
            this.isButtonPressed = false

            if (this.isRecording) {
                this.stopRecording()
            } else if (wasPressed) {
                this.cancelPendingRecording()
            }
        },

        getButtonText() {
            if (this.isConnectingWebSocket) {
                return '连接中...'
            } else if (this.isRecording) {
                return '松开结束'
            } else {
                return '按住说话'
            }
        },

        // 开始录音
        async startRecording() {
            if (!this.isButtonPressed) {
                console.log('按钮已松开，取消录音启动')
                return
            }

            if (!this.isWebSocketConnected || this.isRecording || this.isConnectingWebSocket) {
                console.log('录音启动条件不满足:', {
                    isButtonPressed: this.isButtonPressed,
                    isWebSocketConnected: this.isWebSocketConnected,
                    isRecording: this.isRecording,
                    isConnectingWebSocket: this.isConnectingWebSocket
                })
                return
            }

            try {
                console.log('开始录音...')
                const hasPermission = await this.checkMicrophonePermission()
                if (!hasPermission) {
                    this.stopRecording()
                    return
                }

                this.isConnecting = true
                this.isRecording = true
                this.recordingTime = 0

                if (this.speechService) {
                    this.speechService.reset()
                }

                await this.startRecorder()
            } catch (error) {
                console.error('录音启动失败:', error)
                this.stopRecording()
            }
        },

        // 检查麦克风权限
        async checkMicrophonePermission() {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
                console.log('麦克风权限检查通过')
                stream.getTracks().forEach(track => track.stop())
                return true
            } catch (error) {
                console.error('麦克风权限检查失败:', error)
                this.showConnectionError('无法访问麦克风，请检查权限设置')
                return false
            }
        },

        // 启动录音器
        async startRecorder() {
            if (window.RecorderManager) {
                console.log('创建RecorderManager实例...')
                try {
                    this.recorder = new window.RecorderManager('./dist')
                    console.log('RecorderManager实例创建成功')
                } catch (error) {
                    console.error('创建RecorderManager失败:', error)
                    this.showConnectionError('录音器初始化失败')
                    return
                }

                this.recorder.onStart = () => {
                    console.log('录音开始 - onStart 回调触发')
                    this.isConnecting = false
                    this.startTimer()
                }

                this.recorder.onFrameRecorded = ({ frameBuffer, isLastFrame }) => {
                    console.log('收到音频帧:', frameBuffer.byteLength, 'bytes, isLastFrame:', isLastFrame)
                    
                    // 存储音频数据块
                    this.audioChunks.push(new Uint8Array(frameBuffer))
                    
                    if (this.speechService && this.speechService.ws && this.speechService.ws.readyState === WebSocket.OPEN) {
                        console.log('发送音频数据到WebSocket...')
                        this.speechService.sendAudioData(frameBuffer, isLastFrame)
                    } else {
                        console.warn('WebSocket未连接或speechService不可用', {
                            speechService: !!this.speechService,
                            ws: !!this.speechService?.ws,
                            readyState: this.speechService?.ws?.readyState
                        })
                    }
                }

                this.recorder.onStop = () => {
                    console.log('录音停止回调')
                    this.stopTimer()
                    this.isRecording = false
                    this.isConnecting = false
                    
                    // 处理音频数据
                    this.processAudioData()
                    
                    // 发送结束信号
                    if (this.speechService && this.speechService.ws && this.speechService.ws.readyState === WebSocket.OPEN) {
                        console.log('发送录音结束信号...')
                        this.speechService.sendAudioData(new ArrayBuffer(0), true)
                    }
                }

                console.log('启动录音器...')
                try {
                    await this.recorder.start({
                        sampleRate: 16000,
                        frameSize: 1280
                    })
                    console.log('录音器启动完成')
                } catch (error) {
                    console.error('录音器启动失败:', error)
                    this.showConnectionError('录音器启动失败，请检查麦克风权限')
                    this.stopRecording()
                }
            } else {
                console.error('RecorderManager不可用')
                this.showConnectionError('录音器不可用，请检查页面资源')
            }
        },

        // 处理音频数据
        processAudioData() {
            if (this.audioChunks.length > 0) {
                // 计算音频时长
                this.audioDuration = this.recordingTime
                
                // 合并音频数据块
                const totalLength = this.audioChunks.reduce((sum, chunk) => sum + chunk.length, 0)
                const combinedArray = new Uint8Array(totalLength)
                let offset = 0
                
                for (const chunk of this.audioChunks) {
                    combinedArray.set(chunk, offset)
                    offset += chunk.length
                }
                
                // 创建音频Blob
                this.audioBlob = new Blob([combinedArray], { type: 'audio/wav' })
                console.log('音频处理完成:', {
                    duration: this.audioDuration,
                    blobSize: this.audioBlob.size,
                    chunks: this.audioChunks.length
                })
            }
        },

        stopRecording() {
            console.log('停止录音...')

            this.clearConnectDelay()

            // 重置按钮状态
            this.isButtonPressed = false

            // 先停止录音器
            if (this.recorder) {
                this.recorder.stop()
                this.recorder = null
            }

            // 发送结束信号给WebSocket
            if (this.speechService && this.speechService.ws && this.speechService.ws.readyState === WebSocket.OPEN) {
                this.speechService.sendAudioData(new ArrayBuffer(0), true)
                // 延迟关闭WebSocket连接，确保结束信号发送完成
                setTimeout(() => {
                    console.log('关闭WebSocket连接...')
                    this.speechService.close()
                    this.isWebSocketConnected = false
                    
                    // 录音结束后，发送音频数据和识别结果给父组件
                    if (this.audioBlob && this.audioDuration > 0) {
                        console.log('发送音频数据:', {
                            duration: this.audioDuration,
                            blobSize: this.audioBlob.size,
                            recognizedText: this.recognizedText
                        })
                        
                        // 创建音频URL
                        const audioUrl = URL.createObjectURL(this.audioBlob)
                        
                        this.onResult({
                            type: 'voice',
                            audioBlob: this.audioBlob,
                            audioUrl: audioUrl,
                            duration: this.audioDuration,
                            text: this.recognizedText || ''
                        })
                        
                        // 重置状态
                        this.recognizedText = ''
                        this.isRecognitionComplete = false
                        this.audioChunks = []
                        this.audioDuration = 0
                        this.audioBlob = null
                    }
                }, 1000) // 增加延迟时间，确保识别完成
            }

            this.stopTimer()
            this.isRecording = false
            this.isConnecting = false

            if (this.connectionTimeoutId) {
                clearTimeout(this.connectionTimeoutId)
                this.connectionTimeoutId = null
            }

            if (this.connectionProgressIntervalId) {
                clearInterval(this.connectionProgressIntervalId)
                this.connectionProgressIntervalId = null
            }
        },

        cancelPendingRecording() {
            console.log('取消录音流程...')

            this.isButtonPressed = false
            this.clearConnectDelay()

            if (this.isConnectingWebSocket) {
                this.isConnectingWebSocket = false
                this.connectionProgress = 0
            }

            if (this.isWebSocketConnected && this.speechService) {
                try {
                    this.speechService.close()
                } catch (error) {
                    console.warn('关闭WebSocket连接时出错:', error)
                }
            }

            this.isWebSocketConnected = false
            this.stopTimer()

            if (this.recorder) {
                try {
                    this.recorder.stop()
                } catch (error) {
                    console.warn('取消录音时停止录音器失败:', error)
                }
                this.recorder = null
            }

            this.isRecording = false
            this.isConnecting = false
            this.audioChunks = []
            this.audioDuration = 0
            this.audioBlob = null
            this.recognizedText = ''
            this.isRecognitionComplete = false

            if (this.connectionTimeoutId) {
                clearTimeout(this.connectionTimeoutId)
                this.connectionTimeoutId = null
            }

            if (this.connectionProgressIntervalId) {
                clearInterval(this.connectionProgressIntervalId)
                this.connectionProgressIntervalId = null
            }
        },

        startTimer() {
            this.timeInterval = setInterval(() => {
                this.recordingTime++
                if (this.recordingTime >= 60) {
                    this.stopRecording()
                }
            }, 1000)
        },

        stopTimer() {
            if (this.timeInterval) {
                clearInterval(this.timeInterval)
                this.timeInterval = null
            }
        },

        cleanup() {
            this.stopRecording()
            this.clearConnectDelay()
            // 关闭WebSocket连接
            if (this.speechService) {
                this.speechService.close()
            }
        }
    }
}
</script>

<style scoped>
.voice-recorder {
    position: relative;
    display: inline-block;
}

.voice-btn {
    width: 180px;
    height: 180px;
    border: none;
    border-radius: 50px;
    background: rgba(0, 0, 0, 0.3);
    color: white;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
}
.voice-btn::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg,
    rgba(255, 255, 255, 1),
    rgba(204, 204, 204, 0.8),
    rgba(204, 204, 204, 0.4),
    rgba(204, 204, 204, 0.4),
    rgba(204, 204, 204, 0.8),
    rgba(255, 255, 255, 0.9));
    border-radius: 50px;
    padding: 1px;
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask-composite: xor;
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    z-index: -1;
}


.voice-btn.connecting-websocket .microphone-icon {
    animation: pulse-icon 1.5s infinite;
}

.voice-btn.recording .microphone-icon {
    animation: pulse-icon 0.8s infinite;
    transform: scale(1.1);
}

.voice-btn:not(:disabled):hover .microphone-icon {
    transform: scale(1.05);
}

.microphone-icon {
    width: 88px;
    height: 88px;
    transition: all 0.2s ease;
}

.connection-progress {
    position: absolute;
    top: -40px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(0, 0, 0, 0.8);
    color: white;
    padding: 8px 12px;
    border-radius: 20px;
    font-size: 12px;
    white-space: nowrap;
    z-index: 1000;
}

.progress-bar {
    width: 120px;
    height: 4px;
    background: rgba(255, 255, 255, 0.3);
    border-radius: 2px;
    overflow: hidden;
    margin-bottom: 4px;
}

.progress-fill {
    height: 100%;
    background: #4CAF50;
    border-radius: 2px;
    transition: width 0.3s ease;
}

.progress-text {
    text-align: center;
    font-size: 10px;
}

@keyframes pulse-recording {
    0% { 
        transform: scale(1);
        box-shadow: 0 4px 12px rgba(244, 67, 54, 0.3);
    }
    50% { 
        transform: scale(1.05);
        box-shadow: 0 6px 16px rgba(244, 67, 54, 0.5);
    }
    100% { 
        transform: scale(1);
        box-shadow: 0 4px 12px rgba(244, 67, 54, 0.3);
    }
}

@keyframes pulse-connecting {
    0% { 
        transform: scale(1);
        box-shadow: 0 4px 12px rgba(255, 152, 0, 0.3);
    }
    50% { 
        transform: scale(1.05);
        box-shadow: 0 6px 16px rgba(255, 152, 0, 0.5);
    }
    100% { 
        transform: scale(1);
        box-shadow: 0 4px 12px rgba(255, 152, 0, 0.3);
    }
}

@keyframes pulse-icon {
    0% { 
        transform: scale(1);
        opacity: 1;
    }
    50% { 
        transform: scale(1.1);
        opacity: 0.8;
    }
    100% { 
        transform: scale(1);
        opacity: 1;
    }
}
</style>
